import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { Team } from 'modules/teams/domain/interfaces/team';
import { useTeamList } from 'modules/teams/ui/hooks/use-teams-list';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { TEAM_DEFAULT_LOGO } from 'shared/constants/links';
import { useToastApiError } from 'shared/hooks/use-toast-api-error';
import UDButton from 'ud-ui/components/button';
import HeaderContentCategory from 'ud-ui/components/category/components/list-wrapper/header-content';
import Modal from 'ud-ui/components/modal';
import { UDEntityRow, UDPagedList } from 'ud-ui/components/paged-list';

type Source = 'common' | 'players';

const TeamsPage = () => {
  const { t } = useLingui();
  const toastApiError = useToastApiError();
  const navigate = useNavigate();
  const handleCreateClick = useCallback(() => {
    navigate(Pages.TEAMS.CREATE);
  }, [navigate]);
  const { setHeaderContent } = useMainLayout();

  const createButtonText = t`Добавить команду`;

  const {
    items,
    itemsOffset,
    state,
    page,
    totalPages,
    perPage,
    draftSearch,
    setPage,
    setPerPage,
    setSearch,
    buildPageHref,
    deleteTeam,
  } = useTeamList();

  const [teamToDelete, setTeamToDelete] = useState<Team | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const openDelete = useCallback((team: Team) => {
    setTeamToDelete(team);
  }, []);
  const closeDelete = useCallback(() => {
    setTeamToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!teamToDelete) return;
    setIsDeleting(true);
    try {
      await deleteTeam(teamToDelete.id);
      setTeamToDelete(null);
      toast.success(t`Команда успешно удалена`);
    } catch (error) {
      // 3020 «у команды есть матчи» переводит реестр кодов.
      toastApiError(error, msg`Не удалось удалить команду`);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteTeam, teamToDelete, t, toastApiError]);

  const handleEditInfoClick = useCallback(
    (team: { id: number }, source: Source) => {
      navigate(Pages.TEAMS.builders.edit(team.id, source));
    },
    [navigate],
  );

  useEffect(() => {
    setHeaderContent(
      <HeaderContentCategory title={t`Команды`} createButtonText={createButtonText} createButtonOnClick={handleCreateClick} />,
    );
    return () => setHeaderContent(null);
  }, [handleCreateClick, setHeaderContent, t, createButtonText]);

  // Номер строки продолжается со страницы на страницу: на второй странице при
  // десяти элементах отсчёт идёт с 11-го, а не снова с первого. Считается от
  // `itemsOffset`, а не от `page`: пока едет следующая страница, на экране ещё
  // прошлые элементы, и перенумеровывать их раньше времени нельзя.
  const renderTeam = useCallback(
    (team: Team, index: number) => {
      const createdAt = team.createdAt ?? '-';
      const squadSize = team.players.length;

      return (
        <UDEntityRow
          index={itemsOffset + index + 1}
          avatar={team.logo?.url ?? TEAM_DEFAULT_LOGO}
          title={`${team.name}${team.shortName ? ` (${team.shortName})` : ''}`}
          subtitle={
            <span className="flex gap-4">
              <span>
                <Trans>Дата создания: {createdAt}</Trans>
              </span>
              <span>
                <Trans>Состав: {squadSize}</Trans>
              </span>
            </span>
          }
          actions={
            <UDButton variant="secondary" style={{ width: 144 }} onClick={() => openDelete(team)}>
              <Trans>Удалить</Trans>
            </UDButton>
          }
          onClick={() => handleEditInfoClick(team, 'common')}
        />
      );
    },
    [handleEditInfoClick, openDelete, itemsOffset],
  );

  const teamToDeleteName = teamToDelete?.name;

  return (
    <>
      <UDPagedList<Team>
        items={items}
        renderItem={renderTeam}
        getItemKey={(team) => team.id}
        state={state}
        page={page}
        totalPages={totalPages}
        perPage={perPage}
        onPageChange={setPage}
        onPerPageChange={setPerPage}
        pageHref={buildPageHref}
        search={draftSearch}
        onSearchChange={setSearch}
        searchPlaceholder={t`Поиск по командам`}
        emptyText={t`Команд пока нет`}
        emptyAction={
          <UDButton variant="primary" onClick={handleCreateClick}>
            {createButtonText}
          </UDButton>
        }
        notFoundText={t`Команды не найдены`}
      />

      <Modal.Confirm
        show={Boolean(teamToDelete)}
        title={t`Удалить команду «${teamToDeleteName}»?`}
        description={t`Это действие необратимо.`}
        isLoading={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={closeDelete}
      />
    </>
  );
};

export default TeamsPage;
